import {
  Component,
  OnInit,
  OnDestroy,
  LOCALE_ID,
  input,
  output,
  inject,
  viewChild,
  ElementRef,
} from '@angular/core';
import type { Client } from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { STAFF_STATE, TENANTS_INFO } from '@fitness-force/state';
import type { TENANT_MODEL, TENANTSINTERFACE } from '@fitness-force/models';
import type { STAFFINTERFACE, STATE_STAFF_LIST } from '@fitness-force/models';
import { OmniSearchService } from '@fitness-force/api';
import { Subject, Observable, of, forkJoin } from 'rxjs';
import { takeUntil, shareReplay, catchError, finalize } from 'rxjs/operators';
import { ClientStatus } from '@fitness-force/enum';
import { CONSTANTS_DATA } from '@fitness-force/constants';
import type {
  ClassBookingRequest,
  ClassBookingResponse,
  OmniSearchRequestByCompany,
} from '@fitness-force/models';
import { OmniSearchResponseForClient } from './client-omnisearch.interfaces';
import { AsyncPipe, NgClass, TitleCasePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { DrawerModule } from 'primeng/drawer';
import { TagModule } from 'primeng/tag';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { StyleClassModule } from 'primeng/styleclass';
import { BookAppointmentComponent } from '../schedule/appointment/book-appointment/book-appointment.component';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { CalendarComponent } from '../calendar/calendar.component';
import {
  AuthSevices,
  ClassBookingService,
  ToastMessageService,
} from '@fitness-force/api';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { SkeletonModule } from 'primeng/skeleton';
import { StaffStoreService } from '@fitness-force/api';

interface FilterState {
  centers: string[];
  statuses: string[];
  clientReps: number[];
  sources: string[];
  channels: string[];
  webLeadsOnly: boolean;
}

@Component({
  selector: 'app-client-search-sidebar',
  templateUrl: './client-search-sidebar.component.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DrawerModule,
    AvatarModule,
    CheckboxModule,
    TagModule,
    RippleModule,
    TranslateModule,
    StyleClassModule,
    ShortNamePipe,
    BookAppointmentComponent,
    CalendarComponent,
    DialogModule,
    SkeletonModule,
    AsyncPipe,
    NgClass,
    TitleCasePipe,
  ],
  standalone: true,
})
export class ClientSearchSidebarComponent implements OnInit, OnDestroy {
  private omniSearchService = inject(OmniSearchService);
  private store = inject(Store);
  private classBookingService = inject(ClassBookingService);
  translate = inject(TranslateService);
  private toastMessageService = inject(ToastMessageService);
  private authService = inject(AuthSevices);
  private staffStoreService = inject(StaffStoreService);
  locale = inject(LOCALE_ID);

  readonly visible = input(false);
  readonly searchQuery = input('');
  readonly totalCount = input(100);
  readonly goToClientProfile = input.required<(clientId: number, tab: string) => void>();
  readonly filtersChange = output<FilterState>();
  readonly close = output<void>();

  showMobileFilters = false;
  filter1 = 0;
  expandedClientIds: Set<number> = new Set();
  filteredClients: Client[] = [];
  allClients: Client[] = [];
  loading = false;

  centers: { label: string; value: string; checked?: boolean }[] = [];
  statuses: { label: string; value: string; checked?: boolean }[] = [];
  clientReps: {
    label: string;
    value: number;
    image: string;
    checked?: boolean;
  }[] = [];
  sources: { label: string; value: string; checked?: boolean }[] = [];
  channels: { label: string; value: string; checked?: boolean }[] = [];

  filterState: FilterState = {
    centers: [],
    statuses: [],
    clientReps: [],
    sources: [],
    channels: [],
    webLeadsOnly: false,
  };

  selectedTenant: TENANT_MODEL | null = null;
  allTenants: TENANT_MODEL[] = [];
  allcompanyTenants: TENANT_MODEL[] = [];
  allStaffs: STAFFINTERFACE[] = [];
  private destroy$ = new Subject<void>();

  showAllCenters = false;
  showAllStatuses = false;
  showAllClientReps = false;
  showAllSources = false;
  showAllChannels = false;
  showBookAppointmentPopup: boolean = false;
  clientId: number;
  selectedDateTime: Date = new Date();
  tenantId: number;

  readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');

  currentPage = 1;
  pageSize = 100;
  isLoadingMore = false;
  hasMoreResults = true;
  showBookClassPopup = false;
  isBookingClass = false;

  recentlyViewedClients: Client[] = [];
  readonly MAX_RECENT_VIEWED_ITEMS = 10;

  // Staff cache to prevent repeated API calls
  private staffCache: Map<number, Observable<STAFFINTERFACE>> = new Map();
  // Maximum cache size to prevent memory bloat
  private readonly MAX_CACHE_SIZE = 100;

  trackByFn(index: number, client: Client): number {
    return client.clt_id;
  }

  get visibleCenters() {
    return this.showAllCenters ? this.centers : this.centers.slice(0, 4);
  }

  get visibleStatuses() {
    return this.showAllStatuses ? this.statuses : this.statuses.slice(0, 4);
  }

  get visibleClientReps() {
    return this.showAllClientReps
      ? this.clientReps
      : this.clientReps.slice(0, 4);
  }

  get visibleSources() {
    const filtered = this.showAllSources
      ? this.availableSources
      : this.availableSources.slice(0, 4);
    return filtered;
  }

  get visibleChannels() {
    return this.showAllChannels ? this.channels : this.channels.slice(0, 4);
  }

  ngOnInit() {
    // Subscribe to tenant info — perform initial search once tenants are loaded
    this.store
      .select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenant: TENANTSINTERFACE) => {
        if (tenant.selected_tenant.id > 0) {
          const isFirstLoad = !this.selectedTenant;
          this.tenantId = tenant.selected_tenant.id;
          this.selectedTenant = tenant.selected_tenant;
          this.allTenants = tenant.accessed_tenant;
          this.allcompanyTenants = tenant.all_tenant;
          this.updateCenters();

          // Trigger initial search after tenant data is available
          if (isFirstLoad && this.searchQuery()) {
            this.performSearch();
          }
        }
      });

    // Subscribe to staff list
    this.store
      .select(STAFF_STATE)
      .pipe(takeUntil(this.destroy$))
      .subscribe((staff: STATE_STAFF_LIST) => {
        this.allStaffs = staff.staffList;
        this.updateClientReps();
      });

    // Initialize status filters
    this.statuses = [
      { label: 'Prospects', value: 'Prospect', checked: false },
      { label: 'Active Clients', value: 'Client', checked: false },
      { label: 'Ex Clients', value: 'ExClient', checked: false },
    ];

    // Initialize channel and source filters from CONSTANTS_DATA
    this.channels = CONSTANTS_DATA.CHANNEL_SOURCES.map((channelData) => ({
      label: channelData.channel,
      value: channelData.channel,
      checked: false,
    }));

    // Get all unique sources from all channels
    const allSources = new Set<string>();
    CONSTANTS_DATA.CHANNEL_SOURCES.forEach((channelData) => {
      channelData.source.forEach((source) => allSources.add(source));
    });

    this.sources = Array.from(allSources).map((source) => ({
      label: source,
      value: source,
      checked: false,
    }));
  }

  ngOnDestroy() {
    this.destroy$.next();
    // Clear the cache to free memory
    this.staffCache.clear();
    this.destroy$.complete();
  }

  private updateCenters() {
    if (!this.allTenants?.length) return;

    this.centers = this.allTenants.map((tenant) => ({
      label:tenant.name_primary_language || tenant.name_secondary_language || '',
      value: tenant.id.toString(),
      checked: false,
    }));
  }

  private updateClientReps() {
    if (!this.allStaffs?.length) return;

    this.clientReps = this.allStaffs
      .filter((staff) => staff.can_be_client_rep)
      .map((staff) => ({
        label: staff.name,
        value: staff.id,
        image: staff.ff_picture || '',
        checked: false,
      }));
  }

  private async performSearch() {
    this.loading = true;
    try {
      this.currentPage = 1;
      this.hasMoreResults = true;
      this.allClients = [];
      this.filteredClients = [];
      // When no center filter is selected, send all available tenant IDs
      const tenantIds = this.filterState.centers.length > 0
        ? this.filterState.centers.map((id) => parseInt(id))
        : [];

      const request: OmniSearchRequestByCompany = {
        language: this.locale,
        query: this.searchQuery(),
        tenant_ids: tenantIds,
        status:this.filterState.statuses.length > 0 ? this.filterState.statuses : [],
        client_rep_ids: this.filterState.clientReps,
        source: this.filterState.sources,
        channel: this.filterState.channels,
        show_only_web_leads: this.filterState.webLeadsOnly,
        pagination: {
          page_number: this.currentPage,
          page_size: this.pageSize,
        },
      };

      this.omniSearchService.searchByCompany(request).subscribe({
        next: (response: OmniSearchResponseForClient) => {
          const items = response?.items ?? (response as any)?.search_results?.items ?? [];
          this.hasMoreResults = items.length >= this.pageSize;
          this.allClients = items;
          this.filteredClients = items;

          // Prefetch staff details for visible clients
          this.prefetchStaffDetails(this.filteredClients);

          this.loading = false;
          setTimeout(() => {
            const el = this.scrollContainer()?.nativeElement;
            if (el) el.scrollTop = 0;
          });
        },
        error: (error) => {
          console.error('Error performing search:', error);
          this.allClients = [];
          this.filteredClients = [];
          this.hasMoreResults = false;
          this.loading = false;
        },
      });
    } catch (error) {
      console.error('Error performing search:', error);
      this.allClients = [];
      this.filteredClients = [];
      this.hasMoreResults = false;
      this.loading = false;
    }
  }

  private applyFilters() {
    // this.filteredClients = this.allClients.filter(client => {
    //   const centerMatch = this.filterState.centers.length === 0 ||
    //     (client.clt_home_club_tenant_id && this.filterState.centers.includes(client.clt_home_club_tenant_id.toString()));
    //   const statusMatch = this.filterState.statuses.length === 0 ||
    //     (client.clt_status && this.filterState.statuses.includes(client.clt_status.toString().toLowerCase()));
    //   const repMatch = this.filterState.clientReps.length === 0 ||
    //     (client.clt_rep_id && this.filterState.clientReps.includes(client.clt_rep_id));
    //   const sourceMatch = this.filterState.sources.length === 0 ||
    //     (client.clt_source && this.filterState.sources.includes(client.clt_source.toLowerCase()));
    //   const channelMatch = this.filterState.channels.length === 0 ||
    //     (client.clt_channel && this.filterState.channels.includes(client.clt_channel.toLowerCase()));
    //   const webLeadMatch = !this.filterState.webLeadsOnly ||
    //     (client.clt_track_mode == TrackMode.ClientPurchasePortal || client.clt_track_mode?.toString() == TrackMode[TrackMode.ClientPurchasePortal]);
    //   return centerMatch && statusMatch && repMatch && sourceMatch && channelMatch && webLeadMatch;
    // });
    // this.filtersChange.emit(this.filterState);
  }

  toggleMobileFilters() {
    this.showMobileFilters = !this.showMobileFilters;
  }

  isClientExpanded(clientId: number): boolean {
    return this.expandedClientIds.has(clientId);
  }

  toggleDetails(clientId: number) {
    if (this.expandedClientIds.has(clientId)) {
      this.expandedClientIds.delete(clientId);
    } else {
      this.expandedClientIds.add(clientId);

      // Prefetch staff details for this client if not already cached
      const client = this.filteredClients.find((c) => c.clt_id === clientId);
      if (
        client?.clt_rep_id && !this.staffCache.has(client.clt_rep_id)
      ) {
        this.getSalesRep(client.clt_rep_id)
          .pipe(takeUntil(this.destroy$))
          .subscribe();
      }
    }
  }

  expandClient(clientId: number) {
    if (!this.expandedClientIds.has(clientId)) {
      this.expandedClientIds.add(clientId);

      const client = this.filteredClients.find((c) => c.clt_id === clientId);
      if (
        client?.clt_rep_id &&
        !this.staffCache.has(client.clt_rep_id)
      ) {
        this.getSalesRep(client.clt_rep_id)
          .pipe(takeUntil(this.destroy$))
          .subscribe();
      }
    }
  }

  collapseClient(clientId: number) {
    this.expandedClientIds.delete(clientId);
  }

  closeSidebar() {
    // Clear the cache when sidebar is closed to free memory
    this.staffCache.clear();
    this.close.emit();
  }

  isChecked(type: string, value: string | number): boolean {
    switch (type) {
      case 'center':
        return this.filterState.centers.includes(value.toString());
      case 'status':
        return this.filterState.statuses.includes(value.toString());
      case 'clientRep':
        return this.filterState.clientReps.includes(value as number);
      case 'source':
        return this.filterState.sources.includes(value.toString());
      case 'channel':
        return this.filterState.channels.includes(value.toString());
      default:
        return false;
    }
  }

  onFilterChange(type: string, value: string | number, event: any) {
    const checked = event.checked;
    switch (type) {
      case 'center':
        if (checked) {
          this.filterState.centers.push(value.toString());
        } else {
          this.filterState.centers = this.filterState.centers.filter(
            (v) => v !== value.toString(),
          );
        }
        break;
      case 'status':
        if (checked) {
          this.filterState.statuses.push(value.toString());
        } else {
          this.filterState.statuses = this.filterState.statuses.filter(
            (v) => v !== value.toString(),
          );
        }
        break;
      case 'clientRep':
        if (checked) {
          this.filterState.clientReps.push(value as number);
        } else {
          this.filterState.clientReps = this.filterState.clientReps.filter(
            (v) => v !== value,
          );
        }
        break;
      case 'source':
        if (checked) {
          this.filterState.sources.push(value.toString());
        } else {
          this.filterState.sources = this.filterState.sources.filter(
            (v) => v !== value.toString(),
          );
        }
        break;
      case 'channel':
        if (checked) {
          this.filterState.channels.push(value.toString());
        } else {
          this.filterState.channels = this.filterState.channels.filter(
            (v) => v !== value.toString(),
          );
          // Remove sources that are no longer available
          const availableSourceValues = this.availableSources.map(
            (s) => s.value,
          );
          this.filterState.sources = this.filterState.sources.filter((source) =>
            availableSourceValues.includes(source),
          );
        }
        break;
    }
    this.performSearch();
  }

  onVisibilityFilterChange(event: { checked: boolean }) {
    this.filterState.webLeadsOnly = event.checked;
    this.performSearch();
  }

  getClientStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'prospect':
        return 'text-orange-500 bg-orange-50';
      case 'client':
        return 'text-green-500 bg-green-50';
      case 'exclient':
        return 'text-red-500 bg-red-50';
      default:
        return 'text-gray-500 bg-gray-50';
    }
  }

  getClientStatusHtml(status: string): any {
    const clientStatus = { class: '', text: '' };
    switch (status) {
      case ClientStatus.Prospect.toString():
      case ClientStatus[ClientStatus.Prospect]:
        clientStatus.class = 'bg-pink-50 text-green-500';
        clientStatus.text = 'PROSPECT';
        break;
      case ClientStatus.Trial.toString():
      case ClientStatus[ClientStatus.Trial]:
        clientStatus.class = 'bg-cyan-50 text-cyan-500';
        clientStatus.text = 'TRIAL';
        break;
      case ClientStatus.Client.toString():
      case ClientStatus[ClientStatus.Client]:
        clientStatus.class = 'bg-green-50 text-green-500';
        clientStatus.text = 'MEMBER';
        break;
      case ClientStatus.ClientNonMember.toString():
      case ClientStatus[ClientStatus.ClientNonMember]:
        clientStatus.class = 'bg-yellow-50 text-yellow-500';
        clientStatus.text = 'NON-MEMBER';
        break;
      case ClientStatus.ExClient.toString():
      case ClientStatus[ClientStatus.ExClient]:
        clientStatus.class = 'surface-50 text-500';
        clientStatus.text = 'EX-MEMBER';
        break;
      case ClientStatus.BlockedClient.toString():
      case ClientStatus[ClientStatus.BlockedClient]:
        clientStatus.class = 'bg-bluegray-50 text-bluegray-500';
        clientStatus.text = 'TERMINATED';
        break;
      case ClientStatus.ExClientNonMember.toString():
      case ClientStatus[ClientStatus.ExClientNonMember]:
        clientStatus.class = 'surface-100 text-800';
        clientStatus.text = 'EX-CLIENT NON-MEMBER';
        break;
      default:
        return { class: '', text: '' };
    }
    return clientStatus;
  }

  getSalesRep(staffId: number): Observable<STAFFINTERFACE> {
    if (!staffId) return of(null as unknown as STAFFINTERFACE);

    // Check cache first
    if (!this.staffCache.has(staffId)) {
      // If not in cache, fetch and store the result
      this.staffCache.set(
        staffId,
        this.staffStoreService.getStaffById(staffId).pipe(
          // Share the response with all subscribers to prevent multiple API calls
          shareReplay(1),
          // Handle errors gracefully
          catchError(() => of(null as unknown as STAFFINTERFACE)),
          // Ensure we don't exceed the max cache size
          finalize(() => this.limitCacheSize()),
        ),
      );
    }

    return (
      this.staffCache.get(staffId) || of(null as unknown as STAFFINTERFACE)
    );
  }

  /**
   * Prefetch staff details for all visible clients to improve UX
   * @param clients List of clients to prefetch staff details for
   */
  prefetchStaffDetails(clients: Client[]) {
    if (!clients || clients.length === 0) return;

    // Get unique staff IDs
    const staffIds = new Set<number>();
    clients.forEach((client) => {
      if (client.clt_rep_id && !this.staffCache.has(client.clt_rep_id)) {
        staffIds.add(client.clt_rep_id);
      }
    });

    // If no new staff details to fetch, return
    if (staffIds.size === 0) return;

    // Prefetch all required staff details in parallel
    const requests: Observable<STAFFINTERFACE>[] = [];
    staffIds.forEach((id) => {
      const request = this.staffStoreService.getStaffById(id).pipe(
        shareReplay(1),
        catchError(() => of(null as unknown as STAFFINTERFACE)),
      );
      this.staffCache.set(id, request);
      requests.push(request);
    });

    // Execute all requests in parallel and ignore the result
    if (requests.length > 0) {
      forkJoin(requests).pipe(takeUntil(this.destroy$)).subscribe();
    }
  }

  /**
   * Limit the cache size to prevent memory bloat
   */
  private limitCacheSize() {
    if (this.staffCache.size > this.MAX_CACHE_SIZE) {
      // Get the oldest keys to remove
      const keysToDelete = Array.from(this.staffCache.keys()).slice(
        0,
        this.staffCache.size - this.MAX_CACHE_SIZE,
      );
      keysToDelete.forEach((key) => this.staffCache.delete(key));
    }
  }

  toggleShowMore(
    filterType: 'centers' | 'statuses' | 'clientReps' | 'sources' | 'channels',
  ) {
    switch (filterType) {
      case 'centers':
        this.showAllCenters = !this.showAllCenters;
        break;
      case 'statuses':
        this.showAllStatuses = !this.showAllStatuses;
        break;
      case 'clientReps':
        this.showAllClientReps = !this.showAllClientReps;
        break;
      case 'sources':
        this.showAllSources = !this.showAllSources;
        break;
      case 'channels':
        this.showAllChannels = !this.showAllChannels;
        break;
    }
  }

  getSourcesForChannel(channel: string): string[] {
    const channelData = CONSTANTS_DATA.CHANNEL_SOURCES.find(
      (data) => data.channel.toLowerCase() === channel.toLowerCase(),
    );
    return channelData ? channelData.source : [];
  }

  getCenterName(client: Client): string {
    let center = this.allTenants.find(
      (tenant) =>
        tenant.id === client.clt_home_club_tenant_id ||
        tenant.id === client.clt_createdin_tenant_id,
    );

    if(!center){
      center = this.allcompanyTenants.find(
        (tenant) =>
          tenant.id === client.clt_home_club_tenant_id ||
          tenant.id === client.clt_createdin_tenant_id,
      );
    }
    return center ? center.name_primary_language || center.name_secondary_language || '' : '';
  }

  get availableSources(): { label: string; value: string }[] {
    if (this.filterState.channels.length === 0) {
      return this.sources;
    }

    const allowedSources = new Set<string>();
    this.filterState.channels.forEach((channel) => {
      const sourcesForChannel = this.getSourcesForChannel(channel);
      sourcesForChannel.forEach((source) => allowedSources.add(source));
    });

    return this.sources.filter((source) => allowedSources.has(source.label));
  }

  goToClientProfileClick(clientId: number, tab: string) {
    if (
      tab !== 'overview' &&
      tab !== 'transactions' &&
      tab !== 'subscriptions'
    ) {
      this.showBookAppointmentPopup = false;

      this.close.emit();
    }
    this.goToClientProfile()(clientId, tab);
  }

  showBookAppointment(clientId: number) {
    this.showBookAppointmentPopup = true;
    this.clientId = clientId;
  }

  CloseBookAppointment(event: any) {
    this.showBookAppointmentPopup = false;
  }

  showBookClass(clientId: number) {
    this.showBookClassPopup = true;
    this.clientId = clientId;
  }

  CloseBookClass(event: any) {
    this.showBookClassPopup = false;
  }

  onScrollerScroll(event: Event) {
    const el = event.target as HTMLElement;
    if (!el || this.isLoadingMore || !this.hasMoreResults) return;

    // Fire when user scrolls within 200px of the bottom
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 200;
    if (!nearBottom) return;

    this.isLoadingMore = true;
    this.currentPage++;

    const tenantIds = this.filterState.centers.length > 0
      ? this.filterState.centers.map((id) => parseInt(id))
      : [];

    const request: OmniSearchRequestByCompany = {
      language: this.locale,
      query: this.searchQuery(),
      tenant_ids: tenantIds,
      status: this.filterState.statuses.length > 0 ? this.filterState.statuses : [],
      client_rep_ids: this.filterState.clientReps,
      source: this.filterState.sources,
      channel: this.filterState.channels,
      show_only_web_leads: this.filterState.webLeadsOnly,
      pagination: {
        page_number: this.currentPage,
        page_size: this.pageSize,
      },
    };

    this.omniSearchService.searchByCompany(request).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: OmniSearchResponseForClient) => {
        if (response?.items?.length > 0) {
          this.hasMoreResults = response.items.length >= this.pageSize;
          this.allClients = [...this.allClients, ...response.items];
          this.filteredClients = [...this.filteredClients, ...response.items];
          this.prefetchStaffDetails(response.items);
        } else {
          this.hasMoreResults = false;
        }
        this.isLoadingMore = false;
      },
      error: () => {
        this.hasMoreResults = false;
        this.isLoadingMore = false;
      },
    });
  }

  getEventData(event: any) {
    this.isBookingClass = true;
    const classBookingRequest: ClassBookingRequest = {} as ClassBookingRequest;
    classBookingRequest.client_ids = [this.clientId];
    classBookingRequest.add_to_waitlist = false;
    classBookingRequest.move_waitlist_to_booked = false;
    const promise: Promise<boolean> = new Promise((resolve) => {
      this.classBookingService
        .BookClientsToClass(
          this.tenantId,
          event.classData.id,
          classBookingRequest,
        )
        .subscribe({
          next: () => {
            this.translate
              .get('SCHEDULE.VIEWCLASS.MSG.CLIENT_ADDDED_MSG')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.showBookClassPopup = false;
            this.isBookingClass = false;
            resolve(true);
          },
          error: (error: any) => {
            this.toastMessageService.showError(error);
            this.isBookingClass = false;
            resolve(false);
          },
        });
    });
    return promise;
  }

  closePopup(event: any) {
    this.showBookClassPopup = false;
  }

  // Add a client to recently viewed
  private addToRecentlyViewed(client: Client) {
    if (!client) return;

    // Remove the client if it already exists to avoid duplicates
    const existingClients = this.recentlyViewedClients.filter(
      (existingClient) => existingClient.clt_id !== client.clt_id,
    );

    // If we've reached the maximum, remove the last item
    if (existingClients.length >= this.MAX_RECENT_VIEWED_ITEMS) {
      existingClients.pop(); // Remove the last item
    }

    // Add the new client at the beginning
    this.recentlyViewedClients = [client, ...existingClients];

    localStorage.setItem(
      'recentlyViewedClients_' + this.authService.loggedInStaffId,
      JSON.stringify(this.recentlyViewedClients),
    );
  }
}
